import React from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { Alert, Box, CircularProgress } from '@mui/material';
import { Order } from "@/api/SolarCloudApi";
import { useStripePaymentIntent } from "@/queries/payments/useStripePaymentIntent";
import StripePaymentRequestButton from "./StripePaymentRequestButton";
import { stripePromise } from "@/util/stripe";
import applePayLogo from "@/res/logos/Apple-Pay-Mark/apple-pay-logo.png";

interface ApplePayPaymentFormProps {
  order: Order;
  onSuccess: (order: Order) => void;
  isSubmitting: boolean;
  setIsSubmitting?: (isSubmitting: boolean) => void;
  beforePayment?: () => Promise<boolean>;
}

export default function ApplePayPaymentForm({
  order,
  onSuccess,
  isSubmitting,
  setIsSubmitting,
  beforePayment,
}: ApplePayPaymentFormProps) {
  const { clientSecret, amount, error, loading } = useStripePaymentIntent(order.uuid!, "apple_pay");

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (loading || !clientSecret) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: 'stripe' },
      }}
    >
      <div className="space-y-4 py-4">
        <StripePaymentRequestButton
          type="apple"
          label="SolarCloud Payment"
          amount={amount}
          onSuccess={() => onSuccess(order)}
          logo={applePayLogo}
          clientSecret={clientSecret}
          orderUuid={order.uuid ?? ''}
          beforePayment={beforePayment}
        />
      </div>
    </Elements>
  );
}
